import { useTranslation } from 'react-i18next'
import type { VersionStatus } from './staleness'

interface VersionChipProps {
  status: VersionStatus
  bakedVersion: number
  latestVersion?: number
  hasDraft: boolean
  onClick: () => void
}

const STATUS_STYLES: Record<VersionStatus, string> = {
  'up-to-date': 'bg-emerald-100 text-emerald-800 border-emerald-400 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700',
  'newer-available': 'bg-rose-100 text-rose-800 border-rose-500 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-600',
  'draft-available': 'bg-amber-100 text-amber-800 border-amber-400 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700',
}

/**
 * Module scope can't call `t()`, so the map holds KEYS and the component
 * translates at render (#154) — the same descriptor split badge-state.ts
 * already uses for the block configurator's status badge.
 */
const STATUS_LABEL_KEYS: Record<VersionStatus, string> = {
  'up-to-date': 'templateDesigner.versionChip.upToDate',
  'newer-available': 'templateDesigner.versionChip.newerAvailable',
  'draft-available': 'templateDesigner.versionChip.draftAvailable',
}

export function VersionChip({ status, bakedVersion, latestVersion, hasDraft, onClick }: VersionChipProps) {
  const { t } = useTranslation()
  const titleParts = [t('templateDesigner.versionChip.baked', { version: bakedVersion })]
  if (latestVersion !== undefined) {
    titleParts.push(t('templateDesigner.versionChip.latest', { version: latestVersion }))
  }
  if (hasDraft) titleParts.push(t('templateDesigner.versionChip.draftExists'))
  return (
    <button
      type="button"
      data-testid="version-chip"
      data-status={status}
      onClick={e => {
        e.stopPropagation()
        onClick()
      }}
      title={titleParts.join(' · ')}
      className={[
        'pointer-events-auto inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide shadow-sm',
        STATUS_STYLES[status],
      ].join(' ')}
    >
      {t(STATUS_LABEL_KEYS[status])}
    </button>
  )
}
